import { defineStore } from 'pinia';
import { ref } from 'vue';

import { actionList } from '@/composables/missions/missions-actions/constants';
import { useArchiveOrder } from '@/composables/missions/missions-actions/useArchiveOrder';
import { useCloseOrder } from '@/composables/missions/missions-actions/useCloseOrder';
import { ModalName } from '@/constants/modals';
import { useAuthStore } from '@/modules/auth/store/authStore';
import { MissionCommand } from '@/types/mission';

import {
	type DropdownGeneratorAction,
	DropdownGeneratorActionType,
} from '@/dsComponents/Dropdown/types';
import { useModalStore } from '@/dsComponents/modals/modalStore';

interface CallbackItem {
	id: string;
	callback: () => void | Promise<void>;
}

type MissionActionCardData = {
	missionId: string;
	mission?: unknown;
	[key: string]: unknown;
};

export const useMissionActionsStore = defineStore('missionActions', () => {
	const { archiveOrder: archiveOrderComposable } = useArchiveOrder();
	const modalStore = useModalStore();
	const authStore = useAuthStore();
	const { closeMissionWithoutSendToEvolia } = useCloseOrder();

	const refreshCallbacks = ref<CallbackItem[]>([]);
	const disableRefreshCallbacks = ref(false);

	const addRefreshCallback = (callbackItem: CallbackItem): void => {
		const existingIndex = refreshCallbacks.value.findIndex(
			(item) => item.id === callbackItem.id
		);

		if (existingIndex >= 0) {
			refreshCallbacks.value[existingIndex] = callbackItem;
		} else {
			refreshCallbacks.value.push(callbackItem);
		}
	};

	const removeRefreshCallback = (id: string): boolean => {
		const initialLength = refreshCallbacks.value.length;
		refreshCallbacks.value = refreshCallbacks.value.filter(
			(item) => item.id !== id
		);
		return refreshCallbacks.value.length !== initialLength;
	};

	const isNotSeparator = (type: DropdownGeneratorActionType) =>
		type !== DropdownGeneratorActionType.separator;

	const isSeparator = (
		type: DropdownGeneratorActionType,
		actions: DropdownGeneratorAction[],
		index: number
	) => {
		if (type !== DropdownGeneratorActionType.separator) return false;
		if (!actions[index + 1] || index === 0) return false;
		return true;
	};

	const getMissionActions = (
		missionCommands: MissionCommand[]
	): DropdownGeneratorAction[] => {
		return actionList
			.filter((action) => {
				if (action.type !== 'item' || !action.commandName) {
					return true;
				}
				return missionCommands.includes(action.commandName);
			})
			.filter(
				(missionAction, index, actions) =>
					isNotSeparator(missionAction.type) ||
					isSeparator(missionAction.type, actions, index)
			)
			.map((action) =>
				action.type !== 'item'
					? action
					: {
							...action,
							item: {
								...action.item,
								onClick: async (relatedCardData: MissionActionCardData) => {
									const hasEvoliaOrders = authStore.isAgency;
									if (
										action.commandName === MissionCommand.sendOrderToEvolia &&
										!hasEvoliaOrders
									) {
										return;
									}
									if (action.commandName === MissionCommand.complete) {
										if (hasEvoliaOrders) {
											modalStore.actions.open(ModalName.SendOrderToEvolia, {
												mission: relatedCardData.mission,
												openAfterCloseOrder: true,
											});
										} else {
											await closeMissionWithoutSendToEvolia(
												relatedCardData.missionId
											);
										}
									}
									if (action.commandName === MissionCommand.archive) {
										await archiveOrderComposable(relatedCardData.missionId);
									}
									if (action.modalName) {
										if (action.modalName !== ModalName.SendOrderToEvolia) {
											modalStore.actions.closeAll();
										}
										modalStore.actions.open(action.modalName, {
											...relatedCardData,
											...action.modalProps,
											commandName: action.commandName,
										});
										return;
									}
									if (
										refreshCallbacks.value.length > 0 &&
										!disableRefreshCallbacks.value
									) {
										await Promise.all(
											refreshCallbacks.value.map((item) => item.callback())
										);
									}
									disableRefreshCallbacks.value = false;
									if (action.item?.onClick) {
										action.item.onClick(relatedCardData);
									}
								},
							},
						}
			);
	};

	return {
		getMissionActions,
		addRefreshCallback,
		removeRefreshCallback,
	};
});
